import React, { useState, useEffect } from 'react';
import { Content } from 'styles/globalStyles';
import { TitleBar } from 'styles/layout/titlebar';
import { Container } from 'styles/website/profile';
import { isEmptyOrNull } from 'helpers';
import { WPSForm } from 'styles/layout/forms';
import JsxHelper from 'helpers/jsx';
import DialogHelper from 'helpers/dialog';
import useModal from 'hooks/useModal';
import env from 'config/env';
import WebsiteHelper from 'helpers/website';
import WebsiteService from 'services/website';
import useConfirm from 'hooks/useConfirm';

const NodeJS = ({ website }) =>{
  const [loading, setLoading] = useState(false);
  const [nodejsServerInfo, setNodejsServerInfo] = useState(null);
  const [nodejsConfig, setNodejsConfig] = useState(null);
  const [currentScreen, setCurrentScreen] = useState('main');
  const isNodeJsConnected = () => !isEmptyOrNull(nodejsConfig);
  const modalDialog = useModal();
  const confirm = useConfirm();
  const NODEJS_CONFIG = WebsiteHelper.getNodejsConfigOptions();
  const EMPTY_INPUT = NODEJS_CONFIG.reduce((acc, item) => {
    acc[item.name] = item.default || '';
    return acc;
  }, {});
  const [nodejsInput, setNodejsInput] = useState(EMPTY_INPUT);

  // ------------------------------
  // Lifecycle
  // ------------------------------

  useEffect(() => {
    getNodeJSConfig();
    // eslint-disable-next-line
  }, []);

  // ------------------------------
  // Handlers
  // ------------------------------

  const onChange = (e) => {
    const { name, value } = e.target;
    setNodejsInput({ ...nodejsInput, [name]: value});
  }

  const handleConfigResponse = (res) => {
    window.logHelper.info('NodeJS config response', res);
    setNodejsConfig(res);
  }

  // ------------------------------
  // API
  // ------------------------------

  // Tells whether the NodeJS server is connected or not.
  const getNodeJSConfig = () => {
    setLoading('node-config');
    WebsiteService.getNodeJSConfig({ website_slug: website.slug })
      .then(handleConfigResponse)
      .catch(error => DialogHelper.error(modalDialog, error))
      .finally(() => setLoading(false));
  }

  // Connects the NodeJS server to the website.
  const connectNodeJS = () => {
    setLoading('connect-nodejs');
    const data = {...nodejsInput, website_slug: website.slug };
    WebsiteService.connectNodeJS(data)
      .then((config) => {
        handleConfigResponse(config);
        DialogHelper.success(modalDialog, 'NodeJS server connected successfully.', 'This may take a few minutes to be fully operational.');
      })
      .catch(error => DialogHelper.error(modalDialog, error))
      .finally(() => setLoading(false));
  }

  // Disconnects the NodeJS server from the website.
  const disconnectNodeJS = () => {
    setLoading('disconnect-nodejs');
    WebsiteService.disconnectNodeJS({ website_slug: website.slug })
      .then((config) => {
        setNodejsConfig(config);
        setNodejsInput(EMPTY_INPUT);
        DialogHelper.success(modalDialog, 'NodeJS server disconnected successfully.');
      })
      .catch(error => DialogHelper.error(modalDialog, error))
      .finally(() => setLoading(false));
  }

  // Restarts the NodeJS server.
  const restartNodeJS = () => {
    setLoading('restart-nodejs');
    WebsiteService.restartNodeJS({ website_slug: website.slug })
      .then(setNodejsServerInfo)
      .catch(error => DialogHelper.error(modalDialog, error))
      .finally(() => setLoading(false));
  }

  // Gets extended information about the NodeJS server.
  // This includes the server's status, uptime, and other metrics.
  const getNodeJSInfo = () => {
    setLoading('nodejs-info');
    WebsiteService.getNodeJSInfo({ website_slug: website.slug })
      .then((info) => {
        setNodejsServerInfo(info);
        setCurrentScreen('info');
      })
      .catch(error => DialogHelper.error(modalDialog, error))
      .finally(() => setLoading(false));
  }

  // ------------------------------
  // Render
  // ------------------------------

  const renderConnectFromFragment = () => {
    return (
      <div style={{ maxWidth: '600px' }}>
        {NODEJS_CONFIG.filter(item => !item.hide).map((item, index) => 
          <WPSForm.RowItem key={index} margin='0px'>
            {item.options ? JsxHelper.createSelectInput({
              label: item.title,
              labelClass: 'margin-bottom-12',
              tooltip: item.desc,
              selectClass: 'secondary-box-select',
              name: item.name,
              value: nodejsInput[item.name] || item.default,
              options: item.options,
              required: item.required || false,
              onChange,
              sortOff: true,
            }) : JsxHelper.createTextInput({
              label: item.title,
              labelClass: 'margin-bottom-12',
              tooltip: item.desc,
              name: item.name,
              placeholder: item.placeholder,
              value: nodejsInput[item.name] || item.default,
              required: item.required || false,
              onChange,
            })}
          </WPSForm.RowItem>
        )}
        <div className="action-buttons" style={{ marginTop: '10px' }}>
          {JsxHelper.createButton({
            label: 'Connect Server',
            onClick: connectNodeJS,
            loading: loading === 'connect-nodejs',
          })}
        </div>
      </div>
    );
  }

  const renderNodeJSInfoFragment = () =>  {
    const PROCESS_FIELDS = {
      'node_version': 'Node.js Version',
      'status': 'Server Status',
      'uptime': 'Uptime',
      'port': 'Port',
      'fork_mode': 'Fork Mode',
      'instances': 'Instances',
      'autorestart': 'Auto Restart',
      'cpu': 'CPU Usage',
      'memory': 'Memory Usage',
    };
    return <div className='integration-box info-box'>
      <div className="readonly-box">
        {Object.keys(PROCESS_FIELDS).map((key, index) => (
          <div key={index} className="box-row">
            <span className="box-name">{PROCESS_FIELDS[key]}:</span>
            <span className="box-value">{nodejsServerInfo.process[key]}</span>
          </div>
        ))}
        <div className="box-row">
          <span className="box-name">Error Logs:</span>
          {isEmptyOrNull(nodejsServerInfo.logs.error) && <span className="box-value">No error logs found.</span>}
        </div>
        {!isEmptyOrNull(nodejsServerInfo.logs.error) && <div className="box-row">
          <span className="box-value">
            {nodejsServerInfo.logs.error.map((log, index) => <div key={index} className="log-line">{log}</div>)}
          </span>
        </div>}
        <div className="box-row">
          <span className="box-name">Output Logs:</span>
          {isEmptyOrNull(nodejsServerInfo.logs.output) && <span className="box-value">No output logs found.</span>}
        </div>
        {!isEmptyOrNull(nodejsServerInfo.logs.output) && <div className="box-row">
          <span className="box-value">
            {nodejsServerInfo.logs.output.map((log, index) => <div key={index} className="log-line">{log}</div>)}
          </span>
        </div>}
      </div>
      <div className='action-buttons display-flex-nowrap' style={{display: 'flex'}}>
        {JsxHelper.createButton({
          label: `Refresh Server Info`,
          loading: loading === 'nodejs-info',
          onClick: getNodeJSInfo,
        })}
        {JsxHelper.createButton({
          label: `Restart Server`,
          loading: loading === 'restart-nodejs',
          onClick: restartNodeJS,
        })}
        {JsxHelper.createCloseButton({
          onClick: () => {
            setCurrentScreen('main');
            setNodejsServerInfo(null);
          },
        })}
      </div>
    </div>}

  const renderConnectedFragment = () => {
    const CONFIG_FIELDS = { 'version': 'Node.js Version', 'framework': 'Application Framework', 'entry_file': 'Application Entry File', 'base_url': 'Application Base URL' }
    return <div className='integration-box info-box'>
      <div className="readonly-box">
        {Object.keys(CONFIG_FIELDS).map((key, index) => (
          <div key={index} className="box-row">
            <span className="box-name">{CONFIG_FIELDS[key]}:</span>
            <span className="box-value">
              {key !== 'base_url'
                ? nodejsConfig[key]
                : WebsiteHelper.createLink(website, '', nodejsConfig[key], nodejsConfig[key])
              }
            </span>
          </div>
        ))}
      </div>
      <div className='action-buttons display-flex-nowrap' style={{display: 'flex'}}>
        {JsxHelper.createButton({
          label: `Disconnect Server`,
          onClick: () => {
            DialogHelper
              .confirmAction(confirm, 'disconnect', WebsiteHelper.getLabel(website), 'Node.js Server', 'This will disconnect and stop the Node.js server from your website.')
              .then(disconnectNodeJS)
          },
          loading: loading === 'disconnect-nodejs',
        })}
        {JsxHelper.createButton({
          label: `View Server Info`,
          classes: 'alt--btn',
          loading: loading === 'nodejs-info',
          onClick: getNodeJSInfo,
        })}
        {JsxHelper.createButton({
          label: `Restart Server`,
          loading: loading === 'restart-nodejs',
          onClick: restartNodeJS,
        })}
      </div>
  </div>}

  return (
    <Container className='margin-0'>
      <TitleBar className='titlebar'>
        <TitleBar.Title>Node.js Server</TitleBar.Title>
      </TitleBar>
      <p className='color-primary subheader'>
        {env.getBrandShortName()} makes it easy to connect your application to a Node.js server. This can help integrate more capabilities into your WordPress website.
      </p>
      <Content>
        {loading !== 'node-config'
          ? (isNodeJsConnected()
            ? currentScreen === 'info' ? renderNodeJSInfoFragment() : renderConnectedFragment()
            : renderConnectFromFragment()
          )
          : JsxHelper.createLoadingSpinner()
        }
      </Content>
    </Container>
  );
};

export default NodeJS;
